import React, { useRef, useState, useEffect } from 'react';

const SEGMENT_OPACITY = { entered: 0.25, assigned: 0.5, picking: 0.75, completed: 1.0 };
const capitalize = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function BarChart({ data, selectedWarehouse }) {
	const containerRef = useRef(null);
	const [containerWidth, setContainerWidth] = useState(0);
	const [tooltip, setTooltip] = useState(null);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver(entries => {
			setContainerWidth(entries[0].contentRect.width);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	if (!data.length) {
		return <div className="chart-card__loading">No data</div>;
	}

	const padL = 48;
	const padT = 60;
	const padB = 40;
	const padR = 20;
	const chartH = 200;
	const totalW = containerWidth || 400;

	const slotW = (totalW - padL - padR) / data.length;
	const barW = Math.max(Math.floor(slotW * 0.55), 4);
	const gap = slotW - barW;
	const showLabels = barW >= 14;

	const maxVal = Math.max(...data.map(d => d.value), 1);
	const gridLines = [0, Math.round(maxVal * 0.25), Math.round(maxVal * 0.5), Math.round(maxVal * 0.75), maxVal];

	const handleMouseEnter = (e, d) => {
		const cRect = containerRef.current.getBoundingClientRect();
		const bRect = e.currentTarget.getBoundingClientRect();
		setTooltip({
			x: bRect.left - cRect.left + bRect.width / 2,
			y: bRect.top - cRect.top,
			label: d.warehouse,
			value: d.value,
			segments: d.segments,
		});
	};

	return (
		<div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
			<svg width={totalW} height={chartH + padT + padB} style={{ display: 'block' }}>
				{gridLines.map(g => {
					const y = padT + chartH - (g / maxVal) * chartH;
					return (
						<g key={g}>
							<line
								x1={padL - 8}
								y1={y}
								x2={totalW - padR}
								y2={y}
								stroke="#e4e8ed"
								strokeWidth="1"
								strokeDasharray="4 3"
							/>
							<text
								x={padL - 12}
								y={y + 4}
								textAnchor="end"
								fill="#aab4bf"
								fontSize="11"
								fontFamily="Inter, sans-serif"
							>
								{g}
							</text>
						</g>
					);
				})}

				{data.map((d, i) => {
					const x = padL + i * (barW + gap) + gap / 2;
					const barH = Math.max((d.value / maxVal) * chartH, 2);
					const barY = padT + chartH - barH;
					const dimmed = selectedWarehouse !== 'all' && selectedWarehouse !== d.warehouse;
					const clipId = `bar-clip-${i}`;

					return (
						<g
							key={d.warehouse}
							style={{ cursor: 'pointer' }}
							onMouseEnter={e => handleMouseEnter(e, d)}
							onMouseLeave={() => setTooltip(null)}
						>
							<defs>
								<clipPath id={clipId}>
									<rect x={x} y={barY} width={barW} height={barH} rx="3" ry="3" />
								</clipPath>
							</defs>

							{d.segments ? (
								<g clipPath={`url(#${clipId})`} opacity={dimmed ? 0.25 : 1} style={{ transition: 'opacity 0.2s' }}>
									{(() => {
										let currentY = padT + chartH;
										return [...d.segments].reverse().map(seg => {
											const segH = (seg.value / maxVal) * chartH;
											currentY -= segH;
											return segH > 0 ? (
												<rect
													key={seg.status}
													x={x}
													y={currentY}
													width={barW}
													height={segH}
													fill={d.color}
													opacity={SEGMENT_OPACITY[seg.status]}
												/>
											) : null;
										});
									})()}
								</g>
							) : (
								<rect
									x={x}
									y={barY}
									width={barW}
									height={barH}
									rx="3"
									ry="3"
									fill={d.color}
									opacity={dimmed ? 0.25 : 1}
									style={{ transition: 'opacity 0.2s' }}
								/>
							)}

							{showLabels &&
								d.warehouse.split(' ').map((word, wi) => (
									<text
										key={wi}
										x={x + barW / 2}
										y={padT + chartH + 14 + wi * 11}
										textAnchor="middle"
										fill="#7a8899"
										fontSize="9"
										fontFamily="Inter, sans-serif"
									>
										{word}
									</text>
								))}
						</g>
					);
				})}
			</svg>

			{tooltip && (
				<div
					style={{
						position: 'absolute',
						left: tooltip.x,
						top: tooltip.y - 52,
						transform: 'translateX(-50%)',
						background: '#1a2332',
						borderRadius: 6,
						padding: '6px 10px',
						pointerEvents: 'none',
						whiteSpace: 'nowrap',
						textAlign: 'center',
						opacity: 0.92,
						zIndex: 10,
					}}
				>
					<div style={{ color: 'white', fontSize: 12, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
						{tooltip.label}
					</div>
					{tooltip.segments ? (
						tooltip.segments.map(seg => (
							<div key={seg.status} style={{ color: '#2ec4a5', fontSize: 11, fontFamily: 'Inter, sans-serif' }}>
								{capitalize(seg.status)}: {seg.value.toLocaleString()}
							</div>
						))
					) : (
						<div style={{ color: '#2ec4a5', fontSize: 11, fontFamily: 'Inter, sans-serif' }}>
							{tooltip.value.toLocaleString()} orders
						</div>
					)}
				</div>
			)}
		</div>
	);
}
